import moment, { isMoment } from 'moment'

import { isFunction, isString } from 'lodash'

import { EUROPEAN_DATE, LanguageTags } from '../constants'
import { API_DATE, FULL_DATE_FORMAT, getTimezoneLessMoment } from '../momentjs'
import { Genders } from '../api/student'
import { getIdOrNull, inEnum, isNonEmptyArray, isNull, isObject, isOptimistic, validateString } from '..'
import { RequestPeriod } from './requestPeriod'
import { LanguageLocale } from './language'
import { Student } from './student'
import { Properties } from '../entities'
import { Institution } from './institution'
import { School } from './school'
import { Sector } from './sector'

const BACKEND_OWNED_PROPERTIES = new Set([
  'state',
  'schoolValidatedAt',
  'institutionValidatedAt',
  'dualApprovalRequired',
  'schoolValidationChoice',
  'institutionValidationChoice'
])

export const Tags = Object.freeze({
  INCOMPLETE: { label: 'internship_request.status.incomplete', color: 'orange' },
  ON_HOLD: { label: 'internship_request.status.on_hold', color: 'blue' },
  ACTION_REQUIRED: { label: 'internship_request.status.action_required', color: 'red' },
  ACCEPTED: { label: 'internship_request.status.accepted', color: 'green' },
  REFUSED: { label: 'internship_request.status.refused', color: 'gray' },
  DECLINED_BY_SECTOR: { label: 'internship_request.status.declined_by_sector', color: 'gray' },
  APPROVED_BY_SECTOR: { label: 'internship_request.status.approved_by_sector', color: 'green' },
  AWAITING_SECTOR_APPROVAL: { label: 'internship_request.status.awaiting_sector_approval', color: 'blue' },
  WAITING_FOR_PARTNER_APPROVAL: { label: 'internship_request.status.waiting_for_partner_approval', color: 'blue' }
})

const UNKNOWN_STATE_TAG = Object.freeze({ label: 'internship_request.status.unknown', color: 'gray' })

export const getStateTag = state => Tags[state] ?? UNKNOWN_STATE_TAG

export const States = Object.freeze({
  INCOMPLETE: 'INCOMPLETE',
  ON_HOLD: 'ON_HOLD',
  ACTION_REQUIRED: 'ACTION_REQUIRED',
  ACCEPTED: 'ACCEPTED',
  REFUSED: 'REFUSED',
  DECLINED_BY_SECTOR: 'DECLINED_BY_SECTOR',
  APPROVED_BY_SECTOR: 'APPROVED_BY_SECTOR',
  AWAITING_SECTOR_APPROVAL: 'AWAITING_SECTOR_APPROVAL',
  WAITING_FOR_PARTNER_APPROVAL: 'WAITING_FOR_PARTNER_APPROVAL'
})

export const StateTypes = Object.freeze({
  OPEN: {
    key: 'OPEN',
    values: [
      States.INCOMPLETE,
      States.ON_HOLD,
      States.ACTION_REQUIRED,
      States.AWAITING_SECTOR_APPROVAL,
      States.APPROVED_BY_SECTOR,
      States.DECLINED_BY_SECTOR,
      States.WAITING_FOR_PARTNER_APPROVAL
    ]
  },
  CLOSED: {
    key: 'CLOSED',
    values: [States.ACCEPTED, States.REFUSED]
  }
})

const STATE_GROUPS = [StateTypes.OPEN, StateTypes.CLOSED]

const KNOWN_FILTER_VALUES = new Set([
  ...STATE_GROUPS.map(group => group.key),
  ...STATE_GROUPS.flatMap(group => group.values)
])

const keepKnown = (selection = []) => selection.filter(value => KNOWN_FILTER_VALUES.has(value))

export const expandStateGroups = (selection = []) => [...new Set(
  keepKnown(selection).flatMap(value => STATE_GROUPS.find(group => group.key === value)?.values ?? [value])
)]

const LEGACY_GROUP_STATES = Object.freeze({
  [StateTypes.OPEN.key]: [
    States.INCOMPLETE,
    States.ON_HOLD,
    States.ACTION_REQUIRED,
    States.AWAITING_SECTOR_APPROVAL,
    States.APPROVED_BY_SECTOR,
    States.DECLINED_BY_SECTOR
  ],
  [StateTypes.CLOSED.key]: StateTypes.CLOSED.values
})

export const collapseStateGroups = (selection = []) => STATE_GROUPS.reduce((collapsed, { key, values }) => {
  const legacyStates = LEGACY_GROUP_STATES[key]

  if (collapsed.includes(key) || !legacyStates.every(state => collapsed.includes(state))) {
    return collapsed
  }

  return [key, ...collapsed.filter(state => !values.includes(state) && !legacyStates.includes(state))]
}, keepKnown(selection))

export const Origins = Object.freeze({
  INTERNEO: 'INTERNEO',
  WEB_PORTAL: 'WEB_PORTAL',
  MANUAL: 'MANUAL'
})

/** @enum {string} */
export const InternshipRequestProperties = Object.freeze({
  ACT_TYPE: Properties.ACT_TYPE,
  SCHOOL: Properties.SCHOOL,
  REQUEST_ORIGIN: Properties.REQUEST_ORIGIN,
  SECTION_NAME: Properties.SECTION_NAME,
  SECTION_TO_YEAR: Properties.SECTION_TO_YEAR,
  SCHOOL_ABBREVIATION: Properties.SCHOOL_ABBREVIATION,
  SCHOOL_ADDRESS: Properties.SCHOOL_ADDRESS,
  SCHOOL_COUNTRY: Properties.SCHOOL_COUNTRY,
  SCHOOL_NAME: Properties.SCHOOL_NAME,
  SCHOOL_PHONE: Properties.SCHOOL_PHONE,
  SCHOOL_YEAR_NAME: Properties.SCHOOL_YEAR_NAME,
  INSTITUTION: Properties.INSTITUTION,
  SECTOR: Properties.SECTOR,
  SECTOR_NAME: Properties.SECTOR_NAME,
  REQUEST_PERIODS: Properties.REQUEST_PERIODS,
  COMMENT: Properties.COMMENT,
  DURATION: Properties.DURATION,
  GENDER: Properties.GENDER,
  FIRST_NAME: Properties.FIRST_NAME,
  LAST_NAME: Properties.LAST_NAME,
  EMAIL: Properties.EMAIL,
  INTERNSHIP_MASTER_EMAIL: Properties.INTERNSHIP_MASTER_EMAIL,
  LANGUAGE_LOCALE: Properties.LANGUAGE_LOCALE,
  STUDENT: Properties.STUDENT,
  CREATED_AT: Properties.CREATED_AT,
  BIRTHDAY: Properties.BIRTHDAY,
  PHONE: Properties.PHONE,
  FILES: Properties.FILES,
  METADATA: Properties.METADATA
})

const WITH_ID_PROPERTIES = new Set([
  Properties.ACT_TYPE,
  Properties.SCHOOL,
  Properties.SECTION_TO_YEAR,
  Properties.INSTITUTION,
  Properties.SECTOR,
  Properties.STUDENT,
  Properties.INSTITUTION_COUNTRY,
  Properties.SCHOOL_COUNTRY
])

const PERSONAL_INFO_PROPERTIES = new Set([
  Properties.GENDER,
  Properties.FIRST_NAME,
  Properties.LAST_NAME,
  Properties.EMAIL,
  Properties.BIRTHDAY,
  Properties.NISS
])

export const InternshipRequestValidators = Object.freeze({
  [Properties.ACT_TYPE]: r => isObject(r.actType) || isNull(r.actType),
  [Properties.SCHOOL]: r => getIdOrNull(r.school) !== null || containsPreCreatedSchool(r),
  [Properties.REQUEST_ORIGIN]: r => inEnum(Origins, r.requestOrigin),
  [Properties.SECTION_TO_YEAR]: r => getIdOrNull(r.sectionToYear) !== null,
  [Properties.INSTITUTION]: r => getIdOrNull(r.institution) !== null || containsPreCreatedInstitution(r),
  [Properties.SECTOR]: r => getIdOrNull(r.sector) !== undefined,
  [Properties.REQUEST_PERIODS]: r => isNonEmptyArray(r.requestPeriods),
  [Properties.COMMENT]: r => validateString(r.comment),
  [Properties.GENDER]: r => r.gender == null || inEnum(Genders, r.gender),
  [Properties.FIRST_NAME]: r => isObject(r.student) || validateString(r.firstName, { notEmpty: true }),
  [Properties.LAST_NAME]: r => isObject(r.student) || validateString(r.lastName, { notEmpty: true }),
  [Properties.EMAIL]: r => isObject(r.student) || validateString(r.email, { isEmail: true }),
  /**
   * Only a student ever fills the internship master, and only an unmanaged place is reached by it : whether that
   * address is mandatory is the form's call, this only holds a filled one to a valid format.
   */
  [Properties.INTERNSHIP_MASTER_EMAIL]: r => (r.internshipMasterEmail ?? '') === '' || validateString(r.internshipMasterEmail, { isEmail: true }),
  [Properties.LANGUAGE_LOCALE]: r => r.languageLocale != null
})

export const PortalInternshipStepValidators = Object.freeze({
  [Properties.REQUEST_PERIODS]: r => isNonEmptyArray(r[Properties.REQUEST_PERIODS]),
  [Properties.SECTION_TO_YEAR]: r => getIdOrNull(r.sectionToYear) !== null
})

export const mapToStudent = r => new Student({
  id: r?.student?.id,
  birthdate: r?.birthday,
  email: r?.email,
  firstname: r?.firstName,
  lastname: r?.lastName,
  phoneNumber: r?.phone,
  gender: r?.gender,
  language: r?.languageLocale?.languageTag ?? LanguageTags.EN,
  niss: r?.niss ?? r?.student?.niss
})

export const mergeWithStudent = (request, student) => new InternshipRequest({
  ...request,
  student,
  birthday: student?.birthdate,
  email: student?.email,
  firstName: student?.firstname,
  lastName: student?.lastname,
  phone: student?.phoneNumber,
  gender: student?.gender,
  niss: student?.niss,
  languageLocale: { languageTag: student?.language ?? LanguageTags.EN }
})

/** The institution entity fills whatever it is not given with an empty string : the request carries a null instead. */
const orNull = value => (value ?? '') === '' ? null : value

export const mergeWithInstitution = (request, institution) => ({
  ...request,
  institution: isOptimistic(institution) ? null : institution,
  institutionAcronym: isOptimistic(institution) ? orNull(institution?.acronym) : null,
  institutionAddress: isOptimistic(institution) ? orNull(institution?.address) : null,
  institutionCity: isOptimistic(institution) ? orNull(institution?.city) : null,
  institutionCountry: isOptimistic(institution) ? orNull(institution?.country) : null,
  institutionEmail: isOptimistic(institution) ? orNull(institution?.email) : null,
  institutionName: isOptimistic(institution) ? orNull(institution?.name) : null,
  institutionZipcode: isOptimistic(institution) ? orNull(institution?.zipcode) : null,
  sectorName: isOptimistic(institution) ? orNull(institution?.sector) : null,
  sector: null
})

export const getInstitutionFromRequest = request => {
  const institution = isObject(request.institution)
    ? new Institution({ ...request.institution })
    : new Institution({
      name: request.institutionName,
      acronym: request.institutionAcronym,
      address: request.institutionAddress,
      city: request.institutionCity,
      country: request.institutionCountry,
      email: request.institutionEmail,
      zipcode: request.institutionZipcode
    })

  institution.sector = new Sector({ name: request.sectorName })

  return institution
}

export const mergeWithSchool = (request, school) => ({
  ...request,
  school: isOptimistic(school) ? null : school,
  schoolAbbreviation: isOptimistic(school) ? orNull(school?.abbreviation) : null,
  schoolAddress: isOptimistic(school) ? orNull(school?.address) : null,
  schoolCountry: isOptimistic(school) ? orNull(school?.country) : null,
  schoolName: isOptimistic(school) ? orNull(school?.name) : null,
  schoolPhone: isOptimistic(school) ? orNull(school?.phoneNumber) : null
})

export const getSchoolFromRequest = request => {
  if (isObject(request.school)) {
    return new School({ ...request.school })
  }

  return new School({
    name: request.schoolName,
    abbreviation: request.schoolAbbreviation,
    address: request.schoolAddress,
    country: request.schoolCountry,
    phoneNumber: request.schoolPhone
  })
}

const hasText = value => validateString(value, { notEmpty: true }).valid

const containsPreCreatedSchool = request => hasText(request.schoolName) && hasText(request.schoolAbbreviation)

export const hasDescribedSchool = r => !isObject(r.school) && (hasText(r.schoolName) || hasText(r.schoolAbbreviation))

/** Nothing writes this any more : only requests opened while naming a section was allowed carry one. */
export const hasRequestedSection = r => getIdOrNull(r.sectionToYear) === null && hasText(r.sectionName)

export const needsSchoolCompletion = r => hasDescribedSchool(r) || hasRequestedSection(r)

export const needsInstitutionCompletion = r => r.institution === null

/** A place the school already knows holds no care unit the student named : that name is his alone until it is merged. */
export const needsSectorCompletion = r => isObject(r.institution) && getIdOrNull(r.sector) === null && hasText(r.sectorName)

/** What a student is asked about a place he types in by hand : the acronym, the email and the service are not his. */
const containsPreCreatedInstitution = request => {
  return request.institutionAddress !== null &&
    request.institutionCity !== null &&
    request.institutionCountry !== null &&
    request.institutionName !== null &&
    request.institutionZipcode !== null
}

export const getCreatedAt = r => r.createdAt ? getTimezoneLessMoment(r.createdAt).format(EUROPEAN_DATE) : null
export const getSectionName = r => r.sectionToYear ? r.sectionToYear.sectionName : (r.sectionName ?? null)
export const getSectionAbbreviation = r => r.sectionToYear ? r.sectionToYear.sectionAbbreviation : (r.sectionName ?? null)
export const getInternshipYear = r => r.sectionToYear ? r.sectionToYear.schoolYearName : (r.schoolYearName ?? null)
export const getStartDate = r => isNonEmptyArray(r.requestPeriods) ? getTimezoneLessMoment(r.requestPeriods[0].startDate).format(EUROPEAN_DATE) : null
export const getEndDate = r => isNonEmptyArray(r.requestPeriods) ? getTimezoneLessMoment(r.requestPeriods[0].endDate).format(EUROPEAN_DATE) : null
export const getLastname = r => isObject(r.student) ? r.student.lastname : r.lastName
export const getFirstname = r => isObject(r.student) ? r.student.firstname : r.firstName
export const getEmail = r => isObject(r.student) ? r.student.email : r.email
export const getSectorName = r => {
  let sectorName = null

  if (isObject(r.sector)) {
    sectorName = r.sector.name
  } else if (isString(r.sectorName)) {
    sectorName = r.sectorName
  }

  return sectorName
}
export const getSchoolName = r => {
  let schoolName = null

  if (isObject(r.school)) {
    schoolName = r.school.name
  } else if (isString(r.schoolName)) {
    schoolName = r.schoolName
  }

  return schoolName
}
export const getInstitutionName = r => {
  let institutionName = null

  if (isObject(r.institution)) {
    institutionName = r.institution.name
  } else if (isString(r.institutionName)) {
    institutionName = r.institutionName
  }

  return institutionName
}
export const getComment = r => r.comment
export const getRequestOrigin = r => r.requestOrigin

export const schoolAnswerStands = r => r?.dualApprovalRequired === true && !!r?.schoolValidatedAt
export const institutionAnswerStands = r => r?.dualApprovalRequired === true && !!r?.institutionValidatedAt

const toIdSet = ids => new Set(
  (ids ?? []).map(getIdOrNull).filter(id => id !== null).map(id => `${id}`)
)

export const getPartnerProposal = (request, answeringAsSchool) => {
  const proposal = answeringAsSchool
    ? (institutionAnswerStands(request) ? request?.institutionValidationChoice : null)
    : (schoolAnswerStands(request) ? request?.schoolValidationChoice : null)

  return isObject(proposal) ? proposal : null
}

export const getProposedPeriods = (request, proposal) => {
  const proposed = toIdSet(proposal?.requestPeriods)

  return (request?.requestPeriods ?? []).filter(p => proposed.has(`${p.id}`))
}

export const answerMatchesProposal = (proposal, requestPeriodIds, sector) => {
  if (!isObject(proposal)) {
    return false
  }

  const proposed = toIdSet(proposal.requestPeriods)
  const answered = toIdSet(requestPeriodIds)

  return proposed.size === answered.size &&
    [...answered].every(id => proposed.has(id)) &&
    getIdOrNull(proposal.sector) === getIdOrNull(sector)
}

export class InternshipRequest {
  constructor ({
    id = -1, actType = null, createdAt = moment(), sectionToYear = null, institution = null, lastName = null, firstName = null, email = null, sector = null,
    student = null, gender = Genders.OTHER, school = null, comment = '', duration = null, internshipDurationValue = null, requestOrigin = Origins.MANUAL, requestPeriods = [], phone = null, birthday = null,
    languageLocale = new LanguageLocale(), files = [], institutionAcronym = null, institutionAddress = null, institutionCity = null, institutionCountry = null,
    institutionEmail = null, institutionName = null, institutionZipcode = null, internshipMasterEmail = null, niss = null, sectorName = null, metadata = null, state = States.INCOMPLETE,
    schoolValidatedAt = null, institutionValidatedAt = null, dualApprovalRequired = false, schoolValidationChoice = null, institutionValidationChoice = null,
    schoolAbbreviation = null, schoolAddress = null, schoolCountry = null, schoolName = null, schoolPhone = null,
    sectionName = null, schoolYearName = null
  } = {}) {
    this.id = id
    this.actType = actType
    this.createdAt = getTimezoneLessMoment(createdAt)
    this.sectionToYear = sectionToYear
    this.institution = institution
    this.lastName = lastName
    this.firstName = firstName
    this.gender = gender
    this.email = email
    this.sector = sector
    this.school = school
    this.student = student?.email ? new Student(student) : null
    this.comment = comment
    this.duration = duration ?? internshipDurationValue
    this.requestOrigin = requestOrigin
    this.requestPeriods = isNonEmptyArray(requestPeriods) ? requestPeriods.map(p => new RequestPeriod(p)) : []
    this.phone = phone
    this.birthday = birthday !== null && !isMoment(birthday) ? getTimezoneLessMoment(birthday) : birthday
    this.languageLocale = languageLocale
    this.files = files
    this.institutionAcronym = institutionAcronym
    this.institutionAddress = institutionAddress
    this.institutionCity = institutionCity
    this.institutionCountry = institutionCountry
    this.institutionEmail = institutionEmail
    this.institutionName = institutionName
    this.institutionZipcode = institutionZipcode
    this.internshipMasterEmail = internshipMasterEmail
    this.niss = niss
    this.sectorName = sectorName
    this.metadata = metadata
    this.state = state
    this.schoolValidatedAt = schoolValidatedAt
    this.institutionValidatedAt = institutionValidatedAt
    this.dualApprovalRequired = dualApprovalRequired
    this.schoolValidationChoice = schoolValidationChoice
    this.institutionValidationChoice = institutionValidationChoice
    this.schoolAbbreviation = schoolAbbreviation
    this.schoolAddress = schoolAddress
    this.schoolCountry = schoolCountry
    this.schoolName = schoolName
    this.schoolPhone = schoolPhone
    this.sectionName = sectionName
    this.schoolYearName = schoolYearName
  }

  getStudent = () => this.student?.email ? new Student(this.student) : mapToStudent(this)

  toRequestBody = () => {
    let body = {}

    let studentToSpread = null

    Object.entries(this).forEach(([property, value]) => {
      if (isMoment(value)) {
        const dateFormat = property === Properties.CREATED_AT ? FULL_DATE_FORMAT : API_DATE

        body[property] = value.format(dateFormat)
      } else if (property === Properties.REQUEST_PERIODS) {
        body[property] = this.requestPeriods.map(period => period.toRequestBody())
      } else if (property === Properties.LANGUAGE_LOCALE) {
        body[property] = new LanguageLocale(this.languageLocale).toRequestBody().languageTag
      } else if (BACKEND_OWNED_PROPERTIES.has(property)) {
      } else if (property === Properties.STUDENT && isObject(value) && isOptimistic(value)) {
        studentToSpread = {
          firstName: value.firstname,
          lastName: value.lastname,
          email: value.email,
          gender: value.gender,
          birthday: value.birthdate,
          niss: value.niss
        }
      } else if (WITH_ID_PROPERTIES.has(property)) {
        if (!isOptimistic(value)) {
          body[property] = getIdOrNull(value)
        } else if (
          (!PERSONAL_INFO_PROPERTIES.has(property) && !isFunction(value)) ||
          (PERSONAL_INFO_PROPERTIES.has(property) && this[Properties.STUDENT] === null)
        ) {
          body[property] = value
        }
      } else if (property === Properties.ID && !isOptimistic(value)) {
        body[property] = value
      } else {
        body[property] = value
      }
    })

    if (studentToSpread !== null) {
      body = { ...body, ...studentToSpread }
    }

    return body
  }
}
